/**
 * =========================================================================================
 * ATS - Layout
 * =========================================================================================
 */

import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import Popup from 'reactjs-popup';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import * as Icon from 'ubs-icons';
import {COLOR} from "ubs-framework/v2/common/constant";
import UMenuOptions from 'ubs-framework/v2/UMenuOptions';
import UAvatar from 'ubs-framework/v2/UAvatar';
import USidebarV2 from "ubs-framework/v2/USidebarV2";
import {useEscKey, useOutsideClick} from "ubs-framework/v2/hooks";

import {readCollapsed, writeCollapsed} from "ubs-framework/v2/USidebarV2/storage";

const SIDEBAR_COLLAPSE_KEY = 'recruit_sidebar_collapsed';
// recruit shipped a 90px icon rail before USidebarV2, so an upgrading user with nothing in storage
// still lands on the rail. Their own toggle is what persists from then on.
const SIDEBAR_DEFAULT_COLLAPSED = true;
import {fixed, isInViewport, isMobileDevice, getThumbor, unreadCounter} from "ubs-framework/v2/common/function";
import UPopup from "ubs-framework/v2/UPopup";
import UPaymentBar from "ubs-framework/v2/UPaymentBar";
import ToolBar from "../toolbar";
import UserInfo from './components/user-info';

import {GLOBAL_COMMENT} from "../../../common/constant";
import usePlan from '../../common/hooks/usePlan';
import {PLAN} from 'ubs-framework/v2/common/constant';

const Layout = (props) => {
    const __plan = usePlan();
    const menuref = useRef();
    const sidebarRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const menuOptionsRef = useRef(null);

    const _UT = useSelector(e => e.common.uac?.userType);
    const _UI = useSelector(e => e.common?.userInfo);
    const _ORG_ID = useSelector(e => e.common.orgId);
    const _PLAN = useSelector(e => e?.common?.planAccess);
    const _C_ORG = useSelector(e => e.common.currentOrg);
    const _S3_BASE_URL = useSelector(e => e.common.S3_BASE_URL);
    const _R_ID = useSelector(e => e.common.release?.release?._id);
    const _R_V = useSelector(e => e.common.release?.release?.version);
    const _VL = useSelector(e => e.common.videoUrl);
    const _OSB = useSelector(e => e.ats?.openSidebar);
    const _CC = useSelector(e => e.ats.commentCounter)
    const _P_BAR = useSelector(e => e.ats.paymentbar);
    // UAC
    const _UAC_J = useSelector(e => e?.ats?.uac?.jobs?.modules);
    const _UAC_C = useSelector(e => e?.ats?.uac?.candidates?.modules);
    const _UAC_R = useSelector(e => e?.ats?.uac?.requisition?.modules);
    const _UAC_S = useSelector(e => e?.ats?.uac?.settings?.modules);
    const _UAC_RM = useSelector(e => e?.ats?.uac?.reminder?.modules);
    const _UAC_I = useSelector(e => e?.ats?.uac?.interview?.modules);
    const _UAC_E = useSelector(e => e?.ats?.uac?.employee?.modules);
    const _UAC_RPT = useSelector(e => e?.ats?.uac?.report?.modules);
    const _UAC_RF = useSelector(e => e?.ats?.uac?.referral?.modules);
    const _UAC_PB = useSelector(e => e?.ats?.uac?.preBoarding?.modules);

    const P_S = useCallback((module, type = 'read') => _UAC_S?.[module]?.action?.[type]?.value, [_UAC_S]);
    const [active, setActive] = useState('dashboard');
    const [showSideBar, setShowSideBar] = useState(true);
    const [innerRouteActive, setInnerRouteActive] = useState('');
    const [currentDataMenu, setCurrentDataMenu] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readCollapsed(SIDEBAR_COLLAPSE_KEY, SIDEBAR_DEFAULT_COLLAPSED));
    const [userInfoPopup, setUserInfoPopup] = useState({
        open: false,
        popup: false
    });
    const dataList = [
        {icon: <Icon.GridView/>, name: 'Dashboard', route: 'dashboard', isAccess: true},
        {icon: <Icon.BriefCase/>, name: 'Jobs', route: 'jobs', isAccess: _UAC_J?.jobs?.action?.read?.value},
        {
            icon: <Icon.MultiUser/>,
            name: 'Candidates',
            route: 'candidates',
            include_route: true,
            active: false,
            isAccess: _UAC_C?.candidates?.action?.read?.value,
            inboxBadge: _UAC_C?.candidates?.action?.comment?.value && unreadCounter(_CC, [GLOBAL_COMMENT.ATS_CANDIDATE_CONVERSATION]) ? 1 : 0,
            inner_menu: [
                {
                    main_route: 'candidates',
                    name: 'All Candidates',
                    route: `list`,
                    isAccess: _UAC_C?.candidates?.action?.read?.value,
                },
                {
                    main_route: 'candidates',
                    name: 'Remark Candidates',
                    route: `blocked-candidates`,
                    isAccess: _UAC_C?.remarkCandidate?.action?.read?.value,
                },
                {
                    main_route: 'candidates',
                    name: 'Shared Candidate',
                    route: `shared-candidates`,
                    isAccess: _UAC_C?.sharedCandidate?.action?.read?.value,
                },
                {
                    main_route: 'candidates',
                    name: 'Verification Candidates',
                    route: `verification-candidates`,
                    isAccess: _UAC_C?.verificationCandidate?.action?.read?.value,
                }
            ]
        },

        {icon: <Icon.Watch/>, name: 'Reminder', route: 'reminder', isAccess: __plan(PLAN.reminder) && _UAC_RM?.reminder?.action?.read?.value},
        {
            icon: <Icon.Calendar/>,
            name: 'Interviews',
            route: 'interviews',
            isAccess: __plan(PLAN.interview) && _UAC_I?.interview?.action?.read?.value
        },
        {   icon: <Icon.PreBoarding/>,
            name: 'Preboarding',
            route: 'preboarding',
            isAccess: __plan(PLAN?.preBoarding) && (_UAC_PB?.preBoarding?.action?.read?.value || P_S('preBoardingSignature', "read") ),
            include_route: true,
            active: false,
            inner_menu: [
                {
                    main_route: 'preboarding',
                    name: 'All Candidates',
                    route: `kickoff`,
                    isAccess: _UAC_PB?.preBoarding?.action?.read?.value,
                },
                {
                    main_route: 'preboarding',
                    name: 'Document Signature',
                    route: `document-signature`,
                    isAccess: P_S('preBoardingSignature', "read") ,
                },
                {
                    main_route: 'preboarding',
                    name: 'Offer Letter Approvals',
                    route: `offer-letter-approvals`,
                    isAccess: _UAC_PB?.offerApproval?.action?.read?.value,
                    isPartition: false
                },
                {
                    main_route: 'settings',
                        name: 'Preboarding Settings',
                    route: "preboarding-settings",
                    isAccess: __plan(PLAN?.preBoarding) && ["preBoardingTemplates", "preBoardingSignature", "preBoardingDocuments", "preBoardingFieldTemplates"]?.find(_ => P_S(_)),
                },
            ]
        },
        {
            icon: <Icon.Requisition/>,
            name: 'Requisitions',
            route: 'requisitions',
            isAccess: __plan(PLAN.requisitions) && _UAC_R?.requisition?.action?.read?.value,
            inboxBadge: _UAC_R?.requisition?.action?.comment?.value && unreadCounter(_CC, [GLOBAL_COMMENT.ATS_REQUISITION]) ? 1 : 0,
            inner_menu: [
                {
                    main_route: 'requisitions',
                    name: 'All Requisitions',
                    route: `requisitions`,
                    isAccess: _UAC_R?.requisition?.action?.read?.value,
                },
                {
                    main_route: 'settings',
                    name: 'Requisitions Setting',
                    route: `requisitions-setting/requisition-policy`,
                    settings: true,
                    isAccess: P_S('requisitionSettings'),
                },
            ]
        },
        {
            icon: <Icon.User/>,
            name: 'Employee',
            route: 'employee-list',
            isAccess: !_PLAN?.hrms?.default?.access && _UAC_E?.employee?.action?.read?.value
        },
        {
            icon: <Icon.Referrals/>,
            name: 'Referrals',
            route: 'referral',
            isAccess: __plan(PLAN.referrals) && _UAC_RF?.referral?.action?.read?.value,
        },

        {icon: <Icon.Report/>, name: 'Reports', route: 'reports', isAccess: _UAC_RPT?.report?.action?.read?.value},
        {
            icon: <Icon.Setting fill={COLOR.u_font}/>,
            name: 'Settings', route: 'settings',
            subRoute:
                  P_S('companySetting') ? "company-setting" :
                        true ? "notification" :
                            P_S('integration') ? "integration" :
                                P_S('accessLevel') ? "access-level" :
                                true ? "audit-logs" :
                                    P_S('disqualifiedReason') ? "disqualify-reasons" :
                                        P_S('tagsSource') ? "tags-and-sources" :
                                            P_S('pipelineTemplate') ? "pipeline-templates" :
                                                P_S('customField') ? "custom-fields" :
                                                    P_S('questionBank') ? "question-bank" :
                                                      P_S('emailTemplate') ? "email-templates" :
                                                        P_S('evaluationForm') ? "evaluation-forms" :
                                                            P_S ( 'verificationTemplate') ? "verification-template" :
                                                            P_S('questionnaires') ? "questionnaires" :
                                                                P_S('screeningQuestions') ? "screening-questions" :
                                                                    P_S('eventQuestions') ? "event-questions" :
                                                                        P_S('jobTemplate') ? "job-templates" :
                                                                            P_S('jobTitle') ? "job-title" :
                                                                                P_S('jobType') ? "job-type" :
                                                                                    P_S('experienceLevel') ? "experience-level" :
                                                                                    P_S('department') ? "department" :
                                                                                        P_S('location') ? "location" :
                                                                                            P_S('preBoardingDocuments') ? 'preboarding-settings/documents' :
                                                                                                P_S('preBoardingTemplates') ? 'preboarding-settings/offer-templates' :
                                                                                                    P_S('offerLetterApprovals') ? 'preboarding-settings/offer-approval' :
                                                                                                        P_S('preBoardingSignature') ? 'preboarding-settings/signature-settings' : " ",
            isAccess: true
        },
    ]
    const [list, setList] = useState(dataList);


    useEffect(() => {
        setList(dataList);
    }, [_CC])

    useOutsideClick(menuref, () => {
        if (currentDataMenu) setCurrentDataMenu('');
    });

    useEscKey(() => {
        if (currentDataMenu) setCurrentDataMenu('');

        if (userInfoPopup) setUserInfoPopup({open: false, popup: false});
    });

    useEffect(() => {
        // Handle event listener
        document.querySelector('.u_layout')?.addEventListener('wheel', handleWheel, {passive: true});

        return () => document.querySelector('.u_layout')?.removeEventListener('wheel', handleWheel);

    }, []);

    useEffect(() => {
        setActiveMenu();

        setTimeout(() => {
            if (document.querySelector('.u_active-menu') && !isInViewport(document.querySelector('.u_active-menu'), sidebarRef)) {
                document.querySelector('.u_active-menu').scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }, 10);

    }, [location?.pathname]);

    useEffect(() => {
        if (currentDataMenu) {
            setActive(currentDataMenu?.route);
        } else {
            setActiveMenu();
        }
    }, [currentDataMenu]);

    const handleWheel = (e) => {
        setCurrentDataMenu('');
    }

    const setActiveMenu = () => {

        const paths = location.pathname.split('/');

        if (paths[2] && paths[2].length !== 0) setActive(paths[2]);
        if (paths[3] && paths[3].length !== 0) setInnerRouteActive(paths[3]);

        let atsRoute;
        list?.forEach(li => {
            if (!li?.include_route) {
                atsRoute = li?.inner_menu?.filter(inner => {
                    return inner?.route?.split('/')?.[0] === paths[2];
                });
            }
            if (atsRoute?.length) {
                setActive(atsRoute[0]?.main_route)
                setInnerRouteActive(atsRoute[0]?.route);

            }
        });
    }

    // Menu click
    const menuChange = (param, i, e) => {

        // If menu has inner list
        if (param?.inner_menu && param?.inner_menu?.length !== 0) {
            let u_menu_item = e?.target?.closest('li');

            const windowHeight = window.innerHeight;
            const menuHeight = param?.inner_menu?.filter(_ => _.isAccess === true).length * 35;
            const offsetTop = u_menu_item?.getBoundingClientRect().top;

            let offsetMenuTop = 0, offsetArrowTop = 10;

            if ((menuHeight + offsetTop) > windowHeight) {
                offsetMenuTop = offsetTop - (((menuHeight + offsetTop) - windowHeight) + 30);
                offsetArrowTop = (offsetTop - offsetMenuTop) + 10;
            } else {
                offsetMenuTop = offsetTop;
            }

            setCurrentDataMenu({
                top: offsetMenuTop,
                arrowTop: offsetArrowTop,
                list: param?.inner_menu,
                route: param?.route,
                menu: param
            });
        } else {
            setCurrentDataMenu('');
            if (param?.subRoute) history.push(`/${_ORG_ID}/${param?.route}/${param?.subRoute}`);
            else history.push(`/${_ORG_ID}/${param?.route}`);

            setInnerRouteActive('');
            hideSidebar();
        }
    };

    // Inner menu click
    const innerMenuChange = (e, param, route) => {
        e?.stopPropagation?.();
        const parentMenu = currentDataMenu?.menu || list?.find(_ => _?.route === route);
        let page_route = (parentMenu?.include_route && !param?.include_route) ? `/${_ORG_ID}/${route}/${param.route}` : (param?.settings ? `/${_ORG_ID}/settings/${param.route}` : `/${_ORG_ID}/${param.route}`);
        history.push(page_route);
        setActive(route);
        setCurrentDataMenu('');
        hideSidebar();
    }

    // Get user info popup
    const getUserInfo = () => {
        setUserInfoPopup({open: true});
        setTimeout(() => {
            setUserInfoPopup({open: true, popup: true});
        }, 200);
    }

    const hideSidebar = () => {
        dispatch({type: 'OPEN_SIDEBAR', payload: false});
    }

    const toggleSidebarCollapse = () => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            writeCollapsed(SIDEBAR_COLLAPSE_KEY, next);
            return next;
        });
    }

    const sidebarMenuChange = (param, i, e) => {
        const inner = (param?.inner_menu || []).filter(_ => _?.isAccess);
        if (!inner.length) return menuChange(param, i, e);
        innerMenuChange(e, inner[0], param?.route);
    }

    const onMenuClick = (e) => {
        hideSidebar()
        if (e === 'download') history.push(`/${_ORG_ID}/downloads`);
        if (e === 'release') if (_R_ID) history.push(`/${_ORG_ID}/release-note`)
        if (menuOptionsRef.current) menuOptionsRef.current.close();
    }

    useLayoutEffect(()=>{
        /***
         * hide sidebar and toolbar when candidate portal is open
         * */
        const pathList = ['boarding-document']
        if(pathList?.includes(location.pathname.split('/')[2])){
            setShowSideBar(false)
        }
    },[]);

    return (
        <React.Fragment>
            <div className="u_w-full">
                {_P_BAR?.isPaymentbar && <UPaymentBar title={_P_BAR?.paymentbarContent}/>}
            </div>
            <div className="u_layout">
                {_OSB && <div className="u_left-sidebar-overlay" onClick={hideSidebar}/>}
                {(showSideBar && !['boarding-document']?.includes(location.pathname.split('/')[2])) &&
                    <USidebarV2
                    className="u_left-sidebar"
                    list={list}
                    active={active}
                    innerActive={innerRouteActive}
                    onClick={sidebarMenuChange}
                    onInnerClick={innerMenuChange}
                    open={_OSB}
                    onClose={hideSidebar}
                    collapsed={sidebarCollapsed}
                    collapsible={false}
                    header={
                        <UPopup
                            params={{className: "u_tooltip u_tooltip-full", disabled: isMobileDevice()}}
                            desc={<span>{_C_ORG?.company}</span>}
                        >
                            <div className="u_left-sidebar-logo">
                                <UAvatar name={_C_ORG?.company} image={_C_ORG?.iconURL || _C_ORG?.logoURL} s3Url={_S3_BASE_URL} thumbor={getThumbor()}/>
                                <span className="u_sidebar-org-name">{_C_ORG?.company}</span>
                            </div>
                        </UPopup>
                    }
                    footer={
                    <div className="u_user-detailsview">
                        <Popup
                            nested
                            trigger={_o => (
                                <div className="u_option-icon">
                                    <UPopup
                                        params={{className: "u_tooltip", disabled: isMobileDevice(), nested: true}}
                                        desc={<span>More Options</span>}
                                    >
                                        <span className={`u_icon-rollover ${_o ? 'u_icon-rollover-active' : ''}`}>
                                            <Icon.MoreOptions size={22} fill={COLOR.u_icon_bg}/>
                                        </span>
                                    </UPopup>
                                </div>
                            )}
                            className={sidebarCollapsed ? "u_more_menu" : "u_moremenu"}
                            position="bottom right"
                            closeOnDocumentClick
                            repositionOnResize
                            keepTooltipInside={true}
                            overlayStyle={{zIndex: 100004}}
                            arrow={false}
                            offsetY={sidebarCollapsed ? 60 : -35}
                            offsetX={sidebarCollapsed ? 0 : 45}
                            ref={menuOptionsRef}
                        >
                            <div className="u_menu-options-wpr">
                                <UMenuOptions
                                    active="ats"
                                    chatURL={_PLAN?.chat?.default?.access && `${process.env.REACT_APP_CHAT_MODULE_URL}/${_ORG_ID}`}
                                    PMSURL={_PLAN?.pms?.default?.access && `${process.env.REACT_APP_PMS_MODULE_URL}/${_ORG_ID}`}
                                    HRMSURL={_PLAN?.hrms?.default?.access && `${process.env.REACT_APP_HRMS_MODULE_URL}/${_ORG_ID}`}
                                    PayrollURL={_PLAN?.payroll?.default?.access && `${process.env.REACT_APP_PAYROLL_MODULE_URL}/${_ORG_ID}`}
                                    SurveyURL={[2, 3].includes(_UT) && _PLAN?.survey?.default?.access && `${process.env.REACT_APP_SURVEY_MODULE_URL}/${_ORG_ID}`}
                                    ATSURL={_PLAN?.recruit?.default?.access && `${process.env.REACT_APP_RECRUIT_MODULE_URL}/${_ORG_ID}`}
                                    PerformanceURL={_PLAN?.performance?.default?.access && `${process.env.REACT_APP_PERFORMANCE_MODULE_URL}/${_ORG_ID}`}
                                    VMSURL={_PLAN?.vms?.default?.access && `${process.env.REACT_APP_VMS_MODULE_URL}/${_ORG_ID}`}
                                    AssetsURL={_PLAN?.assets?.default?.access && `${process.env.REACT_APP_ASSETS_MODULE_URL}/${_ORG_ID}`}
                                    onMenuClick={(e) => onMenuClick(e)}
                                    options={['download', 'release', 'appVersion', `${_VL?.link ? "videoUrl" : ""}`]}
                                    version={`10.${fixed(_R_V, 1)}`}
                                    projectTitle="Recruit Video Guidelines"
                                    videoUrl={_PLAN?.recruit?.default?.access && `${_VL?.link}`}
                                />
                            </div>
                        </Popup>

                        <div className="u_user-detail u_flex u_align-center u_content-center">
                            <div className="u_user-view u_flex u_align-center u_cp">
                                <div onClick={getUserInfo}>
                                    {(showSideBar && !['boarding-document']?.includes(location.pathname.split('/')[2])) &&
                                    <UAvatar name={_UI.name} image={_UI?.profileImage} s3Url={_S3_BASE_URL}
                                             thumbor={getThumbor()}/>}
                                </div>
                            </div>
                            <div className="u_user-detail-name">
                                <span className="u_one-line">{_UI?.name}</span>
                            </div>
                        </div>
                    </div>
                    }
                />}

                <div className={`u_content ${_OSB ? 'u_active' : ''}`}>
                    {/** Header **/}
                    {/** hide toolbar when candidate portal open **/}
                    {showSideBar && <ToolBar sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebarCollapse}/>}

                    {props.children}
                </div>

            </div>

            {
                userInfoPopup?.open &&
                <div className={`u_user-info-modal ${userInfoPopup?.popup ? 'u_active' : ''}`}>

                    {userInfoPopup?.popup && <div className="u_user-info-overlay"
                                                  onClick={() => setUserInfoPopup({open: false, popup: false})}/>}

                    <div className="u_user-info-content">

                        <UPopup
                            params={{className: 'u_tooltip',}}
                            desc={<span>Close [Esc]</span>}
                        >
                            <span className="u_user-info-arrow" onClick={() => setUserInfoPopup({open: false, popup: false})}>
                                <Icon.Close fill={COLOR.u_white} size={14}/>
                            </span>
                        </UPopup>

                        <UserInfo />
                    </div>

                </div>
            }

        </React.Fragment>
    );
};

export default Layout;
